import React, { useState } from 'react';

const ImageGallery = ({ images, car }) => {
  const [current, setCurrent] = useState(0);
  const [errors, setErrors] = useState({});
  const [isHovering, setIsHovering] = useState(false);

  const handleError = (index) => setErrors(prev => ({ ...prev, [index]: true }));
  const next = () => setCurrent(p => (p + 1) % images.length);
  const prev = () => setCurrent(p => (p - 1 + images.length) % images.length);

  // Generate descriptive alt text for images
  const getAltText = (index, isThumbnail = false) => {
    if (!car) return isThumbnail ? `Miniatura ${index + 1}` : `Imagen ${index + 1}`;
    
    const baseInfo = `${car.brand} ${car.model} ${car.year}`;
    const features = car.features ? car.features.slice(0, 2).join(', ') : '';
    const description = `${baseInfo} - ${features} - ${car.fuel} - ${car.transmission}`;
    
    return isThumbnail 
      ? `Miniatura ${index + 1} de ${description}`
      : `Foto ${index + 1} de ${description}`;
  };

  if (!images?.length) {
    return (
      <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl h-96 flex items-center justify-center border border-slate-300 shadow-lg">
        <div className="text-center">
          <svg className="w-20 h-20 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-slate-500 font-medium">No hay imágenes disponibles</p>
        </div>
      </div>
    );
  }

  const ArrowIcon = ({ left }) => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={left ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
    </svg>
  );

  const hasError = errors[current];

  return (
    <div className="relative group">
      {/* Main Image Container */}
      <div 
        className="relative rounded-2xl overflow-hidden shadow-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 transition-all duration-300 hover:shadow-3xl"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {hasError ? (
          <div className="w-full h-96 flex items-center justify-center">
            <div className="text-center">
              <svg className="w-20 h-20 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-slate-500 font-medium">Imagen no disponible</p>
            </div>
          </div>
        ) : (
          <img 
            src={images[current]} 
            alt={getAltText(current)} 
            className="w-full h-96 object-contain transition-all duration-500 lg:group-hover:scale-105" 
            onError={() => handleError(current)} 
          />
        )}
        
        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button 
              onClick={prev} 
              className={`absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 ${isHovering ? 'opacity-100 translate-x-0' : 'opacity-80 -translate-x-1'}`}
            >
              <ArrowIcon left />
            </button>
            <button 
              onClick={next} 
              className={`absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-slate-800 rounded-full p-3 shadow-xl transition-all duration-300 hover:scale-110 ${isHovering ? 'opacity-100 translate-x-0' : 'opacity-80 translate-x-1'}`}
            >
              <ArrowIcon />
            </button>
            
            {/* Image Counter */}
            <div className="absolute bottom-4 right-4 bg-gradient-to-r from-black/70 to-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
              {current + 1} / {images.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <div className="flex space-x-3 mt-6 overflow-x-auto pb-2">
          {images.map((img, i) => (
            <button 
              key={i} 
              onClick={() => setCurrent(i)} 
              className={`flex-shrink-0 rounded-xl overflow-hidden border-3 transition-all duration-300 lg:hover:scale-105 ${
                i === current 
                  ? 'border-sky-500 ring-2 ring-sky-300 shadow-lg lg:scale-105' 
                  : 'border-slate-300 lg:hover:border-slate-400 shadow-md'
              }`}
            >
              {errors[i] ? (
                <div className="w-24 h-24 bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                  <svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              ) : (
                <img 
                  src={img} 
                  alt={getAltText(i, true)} 
                  className="w-24 h-24 object-cover transition-all duration-300 hover:scale-110" 
                  onError={() => handleError(i)} 
                />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageGallery;
